import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaCodeBranch, FaChevronDown } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { fetchBranches } from "../../api/branchApi";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "myBranch";

const BranchSelector = ({ onBranchChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [myBranch, setMyBranch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const isInitialLoad = useRef(true);
  const { selectedBranch, setSelectedBranch, setCartTotal } = useAuth();
  const { t, i18n } = useTranslation();
  // Load branches and initialize selected branch
useEffect(() => {
  const loadBranches = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const fetchedBranches = await fetchBranches({ name: "" });
      setBranches(fetchedBranches);

      const storedBranch = localStorage.getItem(STORAGE_KEY);

      if (storedBranch) {
        const parsedBranch = JSON.parse(storedBranch);
        const matchingBranch = fetchedBranches.find(
          (branch) => branch.branch_id === parsedBranch.branch_id
        );
        if (matchingBranch) {
          setMyBranch(matchingBranch);
          setSelectedBranch(matchingBranch);
          onBranchChange?.(matchingBranch);
          return;
        }
      }

      // Detect country only if no stored branch was found
      const countryResponse = await fetch("https://free.freeipapi.com/api/json");
      const countryData = await countryResponse.json();
      const detectedCountry = countryData.countryCode;

      // Try to find matching branch
      const matchedBranch = fetchedBranches.find(
        (branch) => branch.country === detectedCountry
      );

      const defaultBranch =matchedBranch || fetchedBranches[0];// matchedBranch || fetchedBranches[0]; // fallback to first

      if (defaultBranch) {
        setMyBranch(defaultBranch);
        setSelectedBranch(defaultBranch);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultBranch));
        onBranchChange?.(defaultBranch);
      }
    } catch (err) {
      setError("Failed to load branches");
      console.error("Error loading branches:", err);
    } finally {
      setIsLoading(false);
      isInitialLoad.current = false;
    }
  };

  loadBranches();
}, [setSelectedBranch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBranchSelect = useCallback(
    (branch) => {

      if (!isInitialLoad.current && branch.branch_id !== myBranch?.branch_id) {
        localStorage.removeItem("cart");
        setCartTotal(0);
      }

      setMyBranch(branch);
      setSelectedBranch(branch);
      setIsOpen(false);

      // Save to localStorage (null for "All Branches")
      if (branch) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(branch));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }

      // Notify parent component
      onBranchChange?.(branch);
    },
    [onBranchChange, myBranch, setSelectedBranch, setCartTotal]
  );

 
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selector Button */}
     
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`flex items-center justify-between  max-w-xs   px-2 py-2 text-xs  gap-1
          bg-white rounded-lg border shadow-sm hover:bg-gray-50 
          transition-colors duration-150 ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
      >
      <span className="">{t('branches')}</span>
        <div className="flex items-center">
          {myBranch &&  <img
                    src={`https://flagcdn.com/16x12/${myBranch.country.toLowerCase()}.png`}
                    alt={`flag of ${myBranch.country}`}
                    style={{ width: "16px", height: "auto" }}
                    className="mr-1"
                  /> }

        </div>
        <FaChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-150
            ${isOpen ? "transform rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !isLoading && (
        <div
          className="absolute right-0 mt-2 w-56 max-h-60 overflow-y-auto
          bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50"
        >
          {error ? (
            <div className="px-4 py-2 text-sm text-red-600">{error}</div>
          ) : (
            <div className="py-1">
              {/* Selected Branch (if any) */}
              {myBranch && (
                <button
                  onClick={() => handleBranchSelect(myBranch)}
                  className="flex items-center w-full px-4 py-2 text-sm text-left
                    bg-blue-50 text-blue-700"
                >
                  <img
                    src={`https://flagcdn.com/16x12/${myBranch.country.toLowerCase()}.png`}
                    alt={`flag of ${myBranch.country}`}
                    style={{ width: "16px", height: "auto" }}
                    className="mr-3"
                  />
                  {myBranch.name}
                </button>
              )}          

              {/* Other Branch Options */}
              {branches
                .filter(
                  (branch) =>
                    !myBranch || branch.branch_id !== myBranch.branch_id
                )
                .map((branch) => (
                  <button
                    key={branch.branch_id}
                    onClick={() => handleBranchSelect(branch)}
                    className="flex items-center w-full px-4 py-2 text-sm text-left
                      text-gray-700 hover:bg-gray-50"
                  >
                    <img
                      src={`https://flagcdn.com/16x12/${branch.country.toLowerCase()}.png`}
                      alt={`flag of ${branch.country}`}
                      style={{ width: "16px", height: "auto" }}
                      className="mr-3"
                    />
                    {branch.name}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchSelector;
